import { createSlice } from "@reduxjs/toolkit";
import setObj from "lodash/set";
import { setProp } from "Store/Actions";
import { setPropT } from "Store/Actions/ts";
import { AgreementField } from "../ContractDetails";
import { AttachmentDetails } from "../AgreementAttachments";
import { fetchManageAgreement } from "../Services/ManageAgreementThunk";

// ─── State Shape ──────────────────────────────────────────────────────────────

interface ManageAgreementState {
  loading: boolean;
  agreementFields: AgreementField[];
  attachments: AttachmentDetails;
  error: string | null;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: ManageAgreementState = {
  loading: false,
  agreementFields: [],
  attachments: { files: [] },
  error: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const manageAgreementSlice = createSlice({
  name: "manageAgreement",
  initialState,
  reducers: {
    /** Reset the agreement state back to its defaults. */
    resetManageAgreement() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchManageAgreement.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchManageAgreement.fulfilled, (state, action) => {
      state.loading = false;
      state.agreementFields = action.payload.data.agreementFields;
      state.attachments = action.payload.data.attachments;
    });
    builder.addCase(fetchManageAgreement.rejected, (state, action) => {
      state.loading = false;
      state.error =
        (action.payload as string) || "Failed to load agreement details";
    });

    // ── setProp (shared action used across all slices) ────────────────────────
    builder.addCase(setProp, (state, action: setPropT) => {
      if (action.payload.slice === manageAgreementSlice.name) {
        setObj(state, action.payload.key, action.payload.value);
      }
    });
  },
});

export const { resetManageAgreement } = manageAgreementSlice.actions;

export default manageAgreementSlice.reducer;
